// External
import React, { memo, useMemo } from "react";
import _ from "lodash";
import { RadioButton, Tabs } from "@toddle-edu/ds-web";

// Internal
import FullScreenLoader from "UIComponents/FullScreenLoader";

// Relative
import DragAndDropListBox from "../DisplaySettingsList/DragAndDropListBox";
import ManageColumnsDropdown from "../ManageColumnsDropdown/ManageColumnsDropdown";
import GradingPeriodsTab from "../GradingPeriodsTab";
import GenericCustomFieldModal from "TranscriptsV3/components/GenericCustomFieldModal";
import { ASSOCIATION_ENTITY_COLLECTION_KEYS } from "TranscriptsV3/components/GenericCustomFieldModal/modules/constants";
import {
  TAB_KEYS,
  GRADE_SCALE_VIEW_TYPES,
  GRADING_PERIOD_COLUMNS_SECTION_ID,
  ROW_SECTION_IDS_WITH_HEADER_CHECKBOX,
} from "../../modules/constants";
import {
  isRowSectionHeaderChecked,
  TABS_CONTAINER_STYLE,
  toggleAllItemsInSection,
} from "../../modules/utils";
import {
  useConfigureColumnsModalData,
  useDefaultSettingsContentData,
} from "./hooks";

// Styles
import classes from "./DefaultSettingsContent.scss";

const TEST_ID_PREFIX = "transcriptsV3-academicRecords-defaultSettingsContent";
const GRADE_SCALE_VIEW_FOR_EACH_ID = `${TEST_ID_PREFIX}-grade-scale-view-for-each`;
const GRADE_SCALE_VIEW_ACROSS_ID = `${TEST_ID_PREFIX}-grade-scale-view-across`;
const GENERIC_CUSTOM_FIELD_MODAL_TEST_ID = "generic-custom-field-modal";

const ConfigureColumnsModalContent = memo(
  function ConfigureColumnsModalContent({
    academicRecordsData,
    curriculumProgramsData,
    onClose,
    onSave,
    orgGradeScalesData,
    selectedGradeId,
  }) {
    const {
      addColumnWithName,
      addCustomColumn,
      customColumnsViewModel,
      deleteCustomColumn,
      handleSave,
      handleCustomColumnNameChange,
      hasTabs,
      isCustomTab,
      setCustomColumns,
      schoolGridData,
      schoolGridHeaders,
      selectedSchoolName,
      selectedTab: modalSelectedTab,
      setSelectedTab: setModalSelectedTab,
      t: modalT,
      tabOptions,
    } = useConfigureColumnsModalData({
      onSave,
      academicRecordsData,
      curriculumProgramsData,
      transcriptGradeScalesData: orgGradeScalesData,
      selectedGradeId,
    });

    const customGridContent = useMemo(
      () => ({
        type: "customGrid",
        config: {
          associationEntityCollectionKey:
            ASSOCIATION_ENTITY_COLLECTION_KEYS.TRANSCRIPT_GRADE_SCALES,
          customText: modalT("transcript:configure_columns_custom_subtext"),
          deleteTooltipText: modalT(
            "transcript:disabled_delete_entity_tooltip_with_label",
            { label: modalT("common:grade_scale") }
          ),
          emptyRow: {
            value: "",
            onChange: ({ target: { value } }) => addColumnWithName(value),
          },
          items: customColumnsViewModel,
          onItemsUpdate: setCustomColumns,
          onAdd: addCustomColumn,
          onDelete: deleteCustomColumn,
          onValueChange: handleCustomColumnNameChange,
          placeholder: modalT("transcript:column_name"),
        },
      }),
      [
        addColumnWithName,
        addCustomColumn,
        customColumnsViewModel,
        deleteCustomColumn,
        handleCustomColumnNameChange,
        modalT,
        setCustomColumns,
      ]
    );

    const schoolGridContent = useMemo(
      () => ({
        type: "dataGrid",
        config: {
          data: schoolGridData,
          headers: schoolGridHeaders,
          customText: modalT("transcript:configure_columns_school_subtext", {
            schoolName: selectedSchoolName,
          }),
        },
      }),
      [modalT, schoolGridData, schoolGridHeaders, selectedSchoolName]
    );

    const modalContent = isCustomTab ? customGridContent : schoolGridContent;

    return (
      <GenericCustomFieldModal
        testId={GENERIC_CUSTOM_FIELD_MODAL_TEST_ID}
        title={modalT("transcript:configure_custom_columns")}
        onClose={onClose}
        onSave={handleSave}
        tabs={hasTabs ? tabOptions : null}
        selectedTab={modalSelectedTab}
        onTabChange={setModalSelectedTab}
        content={modalContent}
      />
    );
  }
);

const DefaultSettingsContent = ({
  isLoading = false,
  academicRecordsData,
  orgGradeScalesData,
  curriculumProgramsData,
  selectedGradeId,
  onUpdatesChange,
  onRefetchOrgGradeScales,
  draftConfiguration,
  initialListItems,
  initialIsDirty,
  isAcrossYearAggregatesMode = false,
  onListItemsChange,
  gradingPeriodsTabProps,
  effectiveGradingPeriods,
  defaultGradeScalesConfig,
}) => {
  const {
    columnSectionsToRender,
    getColumnSectionHandlers,
    getEnabledKeys,
    handleChildToggle,
    handleLabelUpdate,
    handleReorder,
    handleSaveGradeScales,
    handleToggleItem,
    isConfigureColumnsOpen,
    isUpdatingGradeScales,
    listItems,
    rowSectionViewModel,
    scrollableContentRef,
    selectedGradeScaleView,
    handleGradeScaleViewChange,
    selectedTab,
    setIsConfigureColumnsOpen,
    setSelectedTab,
    hasNoEnabledGradingPeriods,
    shouldShowGradeScaleViewToggle,
    t,
    tabs,
    updateList,
  } = useDefaultSettingsContentData({
    academicRecordsData,
    orgGradeScalesData,
    curriculumProgramsData,
    selectedGradeId,
    onUpdatesChange,
    onRefetchOrgGradeScales,
    draftConfiguration,
    initialListItems,
    initialIsDirty,
    isAcrossYearAggregatesMode,
    onListItemsChange,
    gradingPeriodsTabProps,
    effectiveGradingPeriods,
    defaultGradeScalesConfig,
  });

  const handleCloseConfigureColumnsModal = () => {
    setIsConfigureColumnsOpen(false);
  };
  const handleSelectGradingPeriodWiseView = () => {
    handleGradeScaleViewChange(GRADE_SCALE_VIEW_TYPES.GRADING_PERIOD_WISE);
  };
  const handleSelectAcrossGradingPeriodsView = () => {
    handleGradeScaleViewChange(GRADE_SCALE_VIEW_TYPES.ACROSS_GRADING_PERIODS);
  };
  const firstGradeScaleSectionIndex = _.findIndex(
    columnSectionsToRender,
    section => section.listKey === GRADING_PERIOD_COLUMNS_SECTION_ID
  );

  return (
    <div className={classes.container}>
      {!isAcrossYearAggregatesMode ? (
        <div className={classes.tabsContainer}>
          <Tabs
            options={tabs}
            value={selectedTab}
            type="flexible"
            variant="inline"
            onChange={setSelectedTab}
            hasSeparator={false}
            name={TEST_ID_PREFIX}
            testId={TEST_ID_PREFIX}
            containerStyle={TABS_CONTAINER_STYLE}
          />
        </div>
      ) : null}

      {selectedGradeId && selectedTab === TAB_KEYS.GRADING_PERIODS ? (
        <div className={classes.gradingPeriodsTabWrapper}>
          <GradingPeriodsTab
            periods={gradingPeriodsTabProps?.periods}
            gradeMapId={gradingPeriodsTabProps?.gradeMapId}
            onToggle={gradingPeriodsTabProps?.onToggle}
            onReorder={gradingPeriodsTabProps?.onReorder}
          />
        </div>
      ) : null}

      {!(selectedGradeId && selectedTab === TAB_KEYS.GRADING_PERIODS) ? (
        <div className={classes.scrollableContent} ref={scrollableContentRef}>
          {!isAcrossYearAggregatesMode && selectedTab === TAB_KEYS.COLUMNS ? (
            <div className={classes.listsWrapper}>
              {_.map(columnSectionsToRender, (section, index) => {
                const handlers = getColumnSectionHandlers(section);
                const {
                  onReorder,
                  onToggle,
                  onLabelUpdate,
                  onDelete,
                  onSelectionChange,
                  onSyncStart,
                  onSyncEnd,
                  onOpenSettingsClick,
                } = handlers;
                const selectedKeys = getEnabledKeys(section.renderItems);

                return (
                  <React.Fragment key={section.sectionId}>
                    {shouldShowGradeScaleViewToggle &&
                    !hasNoEnabledGradingPeriods &&
                    section.listKey === GRADING_PERIOD_COLUMNS_SECTION_ID &&
                    index === firstGradeScaleSectionIndex ? (
                      <div className={classes.gradeScaleViewToggleContainer}>
                        <span className={classes.gradeScaleViewToggleLabel}>
                          {t("common:configure_columns")}
                        </span>
                        <div className={classes.gradeScaleViewOptionsContainer}>
                          <div className={classes.gradeScaleViewOption}>
                            <RadioButton
                              id={GRADE_SCALE_VIEW_ACROSS_ID}
                              size="medium"
                              name={`${TEST_ID_PREFIX}-grade-scale-view`}
                              checked={
                                selectedGradeScaleView ===
                                GRADE_SCALE_VIEW_TYPES.ACROSS_GRADING_PERIODS
                              }
                              onChange={handleSelectAcrossGradingPeriodsView}
                            >
                              {t("transcript:across_all_grading_periods")}
                            </RadioButton>
                          </div>
                          <div className={classes.gradeScaleViewOption}>
                            <RadioButton
                              id={GRADE_SCALE_VIEW_FOR_EACH_ID}
                              size="medium"
                              name={`${TEST_ID_PREFIX}-grade-scale-view`}
                              checked={
                                selectedGradeScaleView ===
                                GRADE_SCALE_VIEW_TYPES.GRADING_PERIOD_WISE
                              }
                              onChange={handleSelectGradingPeriodWiseView}
                            >
                              {t("transcript:for_each_grading_period")}
                            </RadioButton>
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <DragAndDropListBox
                      title={section.title}
                      items={section.renderItems || []}
                      onReorder={onReorder}
                      onToggle={onToggle}
                      onLabelUpdate={onLabelUpdate}
                      onDelete={onDelete}
                      manageColumnsContent={
                        <ManageColumnsDropdown
                          selectedGradeId={selectedGradeId}
                          academicRecordsData={academicRecordsData}
                          curriculumProgramsData={curriculumProgramsData}
                          transcriptGradeScalesData={orgGradeScalesData}
                          idPrefix={section.gradingPeriodPrefix}
                          selectedKeys={selectedKeys}
                          onSelectionChange={onSelectionChange}
                          onSyncSuccess={onRefetchOrgGradeScales}
                          onSyncStart={onSyncStart}
                          onSyncEnd={onSyncEnd}
                          isLoading={isLoading}
                        />
                      }
                      showSettings={section.showSettings && !isLoading}
                      onSettingsClick={onOpenSettingsClick}
                      showManageColumns={section.showManageColumns}
                      enableReorder={section.enableReorder}
                      showDragHandle={section.enableReorder}
                      enableInlineEdit={true}
                      showChildItems={false}
                    />
                  </React.Fragment>
                );
              })}
            </div>
          ) : null}

          {isAcrossYearAggregatesMode || selectedTab === TAB_KEYS.ROWS ? (
            <div className={classes.listsWrapper}>
              {_.map(rowSectionViewModel, section => {
                const currentSectionItems =
                  listItems[section.sectionId] || section.items;
                const isHeaderChecked = isRowSectionHeaderChecked({
                  items: currentSectionItems,
                });
                const handleHeaderToggle = isEnabled => {
                  updateList({
                    listKey: section.sectionId,
                    updater: current =>
                      toggleAllItemsInSection({
                        items: current,
                        isEnabled,
                      }),
                  });
                };
                const handleRowReorder = updatedItems => {
                  handleReorder({
                    listKey: section.sectionId,
                    updatedItems,
                  });
                };
                const handleRowToggle = id => {
                  handleToggleItem({ listKey: section.sectionId, id });
                };
                const handleRowChildToggle = (parentId, childId) => {
                  handleChildToggle({
                    listKey: section.sectionId,
                    parentId,
                    childId,
                  });
                };
                const handleRowLabelUpdate = (id, content) => {
                  handleLabelUpdate({
                    listKey: section.sectionId,
                    id,
                    content,
                  });
                };

                return (
                  <DragAndDropListBox
                    key={section.sectionId}
                    title={section.title}
                    items={currentSectionItems}
                    onReorder={handleRowReorder}
                    onToggle={handleRowToggle}
                    onChildToggle={handleRowChildToggle}
                    onLabelUpdate={handleRowLabelUpdate}
                    showSettings={section.showSettings && !isLoading}
                    showManageColumns={section.showManageColumns}
                    enableReorder={section.enableReorder}
                    showDragHandle={section.enableReorder}
                    enableInlineEdit={true}
                    showChildItems={true}
                    showHeaderCheckbox={_.includes(
                      ROW_SECTION_IDS_WITH_HEADER_CHECKBOX,
                      section.sectionId
                    )}
                    isHeaderChecked={isHeaderChecked}
                    onHeaderToggle={handleHeaderToggle}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      ) : null}

      {isUpdatingGradeScales ? <FullScreenLoader /> : null}

      {isConfigureColumnsOpen ? (
        <ConfigureColumnsModalContent
          academicRecordsData={academicRecordsData}
          curriculumProgramsData={curriculumProgramsData}
          onClose={handleCloseConfigureColumnsModal}
          onSave={handleSaveGradeScales}
          orgGradeScalesData={orgGradeScalesData}
          selectedGradeId={selectedGradeId}
        />
      ) : null}
    </div>
  );
};

export default DefaultSettingsContent;
